<script setup lang="ts">
import { App } from '@afinius/api-client/landlord-application/types';
import { useLandlordUserStore } from '@afinius/hooks/store/useLandlordUserStore';
import EnvironmentWarning from '@afinius/organisms/EnvironmentWarning/EnvironmentWarning.vue';
import ImpersonateWarning from '@afinius/organisms/ImpersonateWarning/ImpersonateWarning.vue';
import Title from '@afinius/organisms/Title/Title.vue';
import AfiniusLogo from '@afinius/theme/logos/afinius/afinius.svg?component';
import { Color } from '@afinius/types/Color';
import Avatar from '@afinius/ui-framework/Avatar/Avatar.vue';
import Cluster from '@afinius/ui-framework/Cluster/Cluster.vue';
import Container from '@afinius/ui-framework/Container/Container.vue';
import Dropdown from '@afinius/ui-framework/Dropdown/Dropdown.vue';
import DropdownAction from '@afinius/ui-framework/Dropdown/DropdownAction.vue';
import DropdownBody from '@afinius/ui-framework/Dropdown/DropdownBody.vue';
import DropdownFooter from '@afinius/ui-framework/Dropdown/DropdownFooter.vue';
import DropdownHeader from '@afinius/ui-framework/Dropdown/DropdownHeader.vue';
import Icon from '@afinius/ui-framework/Icon/Icon.vue';
import Typography from '@afinius/ui-framework/Typography/Typography.vue';
import { redirectToApp } from '@afinius/utilities/app-url';
import { ArrowRightStartOnRectangleIcon, ChevronDownIcon, UserIcon } from '@heroicons/vue/24/solid';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const landlordUserStore = useLandlordUserStore();
</script>

<template>
  <header class="header">
    <div class="header__top">
      <Container width="lg">
        <Cluster alignItems="center" justifyContent="space-between">
          <Title class="header__title" size="sm" />

          <Dropdown class="header__dropdown">
            <template #trigger>
              <div class="header__account">
                <Cluster :gap="0.25" alignItems="center">
                  <Avatar
                    :avatar="landlordUserStore.user?.avatar"
                    :initials="landlordUserStore.user?.initials"
                  />

                  <Icon :color="Color.gray500">
                    <ChevronDownIcon />
                  </Icon>
                </Cluster>
              </div>
            </template>

            <template #content>
              <DropdownHeader>
                <Typography tag="h5" type="heading" size="xxs" bold>
                  {{ landlordUserStore.user?.name }}
                </Typography>
              </DropdownHeader>

              <DropdownBody>
                <DropdownAction :to="{ name: 'personal-settings.profile' }">
                  <Cluster alignItems="center" :gap="0.5">
                    <Icon :color="Color.gray500">
                      <UserIcon />
                    </Icon>

                    {{ t('personal-settings.personal-settings') }}
                  </Cluster>
                </DropdownAction>
              </DropdownBody>

              <DropdownFooter>
                <DropdownAction color="error" :onClick="() => redirectToApp(App.AfiniusAuth)">
                  <Cluster alignItems="center" :gap="0.5">
                    <Icon :color="Color.errorBase">
                      <ArrowRightStartOnRectangleIcon />
                    </Icon>

                    {{ t('auth.switch-tenant') }}
                  </Cluster>
                </DropdownAction>
              </DropdownFooter>
            </template>
          </Dropdown>
        </Cluster>
      </Container>

      <EnvironmentWarning />
      <ImpersonateWarning />
    </div>

    <div class="header__nav">
      <Container width="lg">
        <Cluster alignItems="center" :gap="1">
          <AfiniusLogo class="header__nav-logo" />
        </Cluster>
      </Container>
    </div>
  </header>
</template>

<style lang="scss" scoped>
@use './AppHeaderSegment.scss';
</style>
